/**
 * The MarkersView allows to display search services results on a map. It is based on Lefleat's
 * [marker cluster plugin](https://github.com/Leaflet/Leaflet.markercluster). The default plugin functionality is
 * enhanced with handling TomTom services response or searchBox widget events.
 *
 * The tomtom.markersView() method creates a new instance of MarkersView.
 *
 * @example
 * ```javascript
 *
 * tomtom.key("<your-tomtom-API-key>");
 * var map = tomtom.map("map");
 * var searchBoxInstance = tomtom.searchBox().addTo(map);
 * var markersLayer = tomtom.markersView().addTo(map);
 *
 * searchBoxInstance.on(searchBoxInstance.Event.ResultsFound, function (results) {
 *     markersLayer.clearLayers();
 *     markersLayer.add(results);
 * });
 * ```
 *
 * @module Widgets
 * @class markersView
 * @extends L.MarkerClusterGroup
 */
define('widgets/views/markers',['display/map', 'leaflet.markercluster'], function(L) {
    'use strict';
    var MarkersLayer;

    function isArray(array) {
        return array && array.constructor.toString().indexOf('Array') > -1;
    }

    function hasDataArray(points) {
        return typeof points === 'object' && isArray(points.data);
    }

    function convertPoints(points) {
        if (hasDataArray(points)) {
            return points.data;
        }
        if (isArray(points)) {
            return points;
        }
        return [points];
    }

    function getFreeFormAddress(object) {
        return object.address ? object.address.freeformAddress : null;
    }

    function getTextContent(object) {
        return object.element ? object.element.textContent : null;
    }

    function oneOf(first, second, third) {
        return first || second || third;
    }

    function getPoiName(object) {
        if (object.poi && object.poi.name) {
            return object.poi.name + ' ';
        }
        return '';
    }

    function getText(object) {
        var freeformAddress = getFreeFormAddress(object),
            textContent = getTextContent(object),
            text = oneOf(object.text, freeformAddress, textContent),
            poiName = getPoiName(object);
        return poiName + text;
    }

    function hadDataPosition(object) {
        return object.data && object.data.position;
    }

    function getObjectType(object) {
        return object.data ? object.data.type : object.type || 'unknown';
    }

    function getPointGeometry(object) {
        var result = {}, poiObject;
        if (isArray(object.point)) {
            result.position = {lat: object.point[0], lng: object.point[1]};
        } else if (object.position) {
            result.position = {lat: object.position.lat, lng: object.position.lon};
        } else if (hadDataPosition(object)) {
            result.position = {lat: object.data.position.lat, lng: object.data.position.lon};
        }
        result.type = getObjectType(object.data || object);

        poiObject = (object.data || object).poi;
        if (poiObject) {
            if (poiObject.categories) {
                result.poiCategories = poiObject.categories;
            }

            if (poiObject.classifications) {
                result.poiClassifications = poiObject.classifications;
            }
        }

        return result;
    }

    function convertPoint(object) {
        var result, text;
        if (!object) {
            return {};
        }
        result = getPointGeometry(object);
        text = getText(object);

        if (text) {
            result.text = text;
        }
        return result;
    }

    function isEachIconAFunction(options) {
        return options && options.eachIcon && typeof options.eachIcon === 'function';
    }

    function createMarker(object, options) {
        var point = convertPoint(object);
        var marker;
        if (!point || !point.position) {
            return null;
        }
        if (isEachIconAFunction(options)) {
            options.icon = options.eachIcon(point);
        }
        marker = L.marker(point.position, options);
        if (point.text) {
            marker.bindPopup(point.text);
        }
        return marker;
    }

    MarkersLayer = L.MarkerClusterGroup.extend({
        /**
         * Method used to clear all the markers. It is equivalent of clearLayers method.
         *
         * @method remove
         *
         * @return {markersView} The control instance.
         */
        remove: function() {
            this.clearLayers();
            return this;
        },
        /**
         * Method used to add new markers based on the TomTom search service response.
         * [Further reference](http://leafletjs.com/reference.html#marker-options).
         *
         * @method add
         * @param {Object} points The response from search service or event from searchBox widget.
         * @param {Object} [options] The options to be used when creating a marker.
         * @param {Function} [options.eachIcon] The function called to create each icon. It has one argument which is
         *     the point - object containing text, position, type,
         *     poiCategories (deprecated, use poiClassifications) if the type is _poi_,
         *     and poiClassifications if the type is _poi_
         * @return {markersView} The control instance.
         */
        add: function(points, options) {
            var data = convertPoints(points),
                i,
                marker;
            for (i = 0; i < data.length; i += 1) {
                marker = createMarker(data[i], options);
                if (marker) {
                    this.addLayer(marker);
                }
            }
            return this;
        }
    });

    /**
     * The method to instantiates the markersView control.
     * The options are described [here](https://github.com/Leaflet/Leaflet.markercluster/tree/v1.0.4#all-options).
     * @method markersView
     * @param {Object} [options] Options to be passed to the markers view control.
     *
     * @example
     * ```javascript
     *
     *  // SearchBox with location button
     *  var options = {
     *      location: true
     *  };
     * var searchBoxInstance = searchBox(options).addTo(map);
     * ```
     */
    return function(options) {
        return new MarkersLayer(options);
    };
})
;

